<template>
    <div>
        <div class="alert alert-brand my-4">
            Nu återstår bara slutsigneringen. Vänligen öppna BankID via knappen
            nedan.
        </div>
        <div v-if="isLocalhost" class="mb-2">
            <h6>DEBUG</h6>
            <p>Item ID: {{ itemId }}</p>
            <p>Item Description: {{ itemDescription }}</p>
        </div>

        <button
            v-if="!signing"
            type="button"
            class="signatureComponentSignButton btn btn-secondary mt-3"
            @click.prevent="sign"
        >
            {{ signatureButtonLabel }}
        </button>

        <button
            v-else-if="!signatureRequest"
            type="button"
            class="signatureComponentSignButton btn btn-primary mt-3 wait"
        >
            <div class="spinner-border spinner-border-sm mr-2"></div>
        </button>

        <bank-id-v2-prompt
            v-else
            class="mt-3"
            @completed="onCompleted"
            @failed="onFailed"
            :request="signatureRequest"
            :quote-key="quoteKey"
        ></bank-id-v2-prompt>

        <div
            class="mt-3 text-center"
            v-show="usesEmailConsent && emailConsentText"
        >
            <sb-form-checkbox v-model="emailConsent" name="email-consent">
                {{ emailConsentText }}
            </sb-form-checkbox>
        </div>
    </div>
</template>

<style lang="scss" scoped>
button {
    width: 100%;

    &.wait .spinner-border {
        vertical-align: middle;
    }
}
</style>

<script setup>
import axios from "axios";
import { computed, onMounted, ref, watch } from "vue";
import { route } from "ziggy";

import SbFormCheckbox from "../sb/SbFormCheckbox.vue";

// Props should be in sync with onScreenSign.vue
const props = defineProps({
    quoteKey: { type: String, required: true },
    itemId: { type: String, default: undefined },
    itemDescription: { type: String, default: undefined },
    appUrl: { type: String, default: undefined },
    redirectOnSignedUrl: { type: String, default: undefined },
    signeeName: { type: String, default: null },
    signatureButtonLabel: { type: String, default: undefined },
    autostart: { type: Boolean, default: false },
});
const emit = defineEmits(["signed"]);

const signing = ref(false);
const signatureRequest = ref(null);
const emailConsentText = ref(null);
const emailConsent = ref(null);
const usesEmailConsent = ref(false);

const isLocalhost = computed(() => document.location.hostname === "localhost");

async function sign() {
    if (signing.value) {
        return;
    }
    signing.value = true;

    try {
        const response = await axios.post(
            route("bankid_signature_request.store"),
            {
                ref_id: props.itemId,
                message: props.itemDescription,
            },
            {
                headers: {
                    "X-Quote-Key": props.quoteKey,
                },
            }
        );
        signatureRequest.value = response.data;
    } catch (error) {
        signing.value = false;
        console.warn(error);
    }
}

async function onCompleted() {
    if (props.redirectOnSignedUrl) {
        window.location.replace(props.redirectOnSignedUrl);
    } else {
        emit("signed");
    }
}

function onFailed() {
    signing.value = false;
    signatureRequest.value = null;
}

watch(
    () => props.autostart,
    async (value) => {
        if (value) {
            await sign();
        }
    }
);

watch(emailConsent, async (value) => {
    try {
        await axios.post(route("quotes.emailConsent.update", props.quoteKey), {
            consent: value,
        });
    } catch (error) {
        console.warn(error);
    }
});

onMounted(async () => {
    if (props.autostart) {
        await sign();
    }

    try {
        const response = await axios.get(
            route("quotes.emailConsent.get", props.quoteKey)
        );
        usesEmailConsent.value = response.data.usesConsent;
        emailConsentText.value = response.data.text;
        emailConsent.value =
            response.data.consented === null
                ? true
                : response.data.consented;
    } catch (error) {
        console.warn(error);
    }
});
</script>
